/**
 * Strips all non-digit characters from a phone string.
 */
export function stripPhoneToDigits(phone: string | null | undefined): string {
    if (!phone) return '';
    return phone.replace(/\D/g, '');
}

/**
 * Builds a tel: URL for dialing. Preserves leading + for E.164 numbers.
 */
export function phoneToTelHref(phone: string | null | undefined): string | null {
    if (!phone?.trim()) return null;
    const t = phone.trim();
    if (t.startsWith('+')) {
        const digits = t.slice(1).replace(/\D/g, '');
        return digits ? `tel:+${digits}` : null;
    }
    const digits = stripPhoneToDigits(t);
    return digits ? `tel:${digits}` : null;
}

/**
 * Formats a phone number for display.
 * - 10 digits → (xxx) xxx-xxxx
 * - 11 digits starting with 1 → (xxx) xxx-xxxx
 * - Otherwise returns original string
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
    if (!phone) return '';
    const digits = stripPhoneToDigits(phone);
    if (digits.length === 10) {
        return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
        return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
}

/**
 * US emergency numbers: 911 and 933 (the test line that reads back your registered address).
 *
 * Numbers bought through this app do NOT have emergency calling — enabling it at Telnyx needs a
 * validated emergency address per number, and even then a 911 call routes to the PSAP covering
 * that fixed address, which is the wrong one for a technician in a truck. So these are refused
 * outright rather than dialled and silently failing, which is the realistic way someone gets hurt.
 */
export function isEmergencyNumber(phone: string | null | undefined): boolean {
    if (!phone) return false;
    const digits = stripPhoneToDigits(phone).replace(/^1/, '');
    return digits === '911' || digits === '933';
}

export const EMERGENCY_CALL_MESSAGE =
    'Emergency calls are not supported from this app. Use your mobile phone to call 911.';

/**
 * Whether a number may be used to SEND from, right now.
 *
 * Use these two at every "send from" / "call from" picker rather than checking capabilities
 * alone: an inactive number (deactivated, or released back to the provider) still appears in
 * history by design, and offering it means the user only finds out it is dead when the server
 * rejects the send. Filter dropdowns are the deliberate exception — history must stay reachable.
 */
export const canSendSmsFrom = (n: { isActive: boolean; capabilitiesSms?: boolean }) =>
    n.isActive && n.capabilitiesSms !== false;

export const canCallFrom = (n: { isActive: boolean; capabilitiesVoice?: boolean }) =>
    n.isActive && !!n.capabilitiesVoice;

export const SMS_NOT_REGISTERED_WARNING =
    'This number is not registered for business texting yet, so carriers may block or filter its messages. ' +
    'An admin can register it in Settings → Text Registration.';

export const SMS_REGISTRATION_PENDING_WARNING =
    'This number\'s business texting registration is still in progress, so carriers may filter its messages until it completes.';

/**
 * Warning for a send-from number that is not on an approved texting (10DLC) campaign, or null.
 * Warn-only by design: sending stays allowed, so this must never feed canSendSmsFrom. Numbers from
 * other providers, and servers that predate the field, get no warning.
 */
export const smsCampaignWarning = (n: {
    isActive: boolean;
    capabilitiesSms?: boolean;
    provider?: string;
    dlc10AssignmentStatus?: string;
} | null | undefined): string | null =>
    n && canSendSmsFrom(n) && n.provider === 'Telnyx'
        && n.dlc10AssignmentStatus !== undefined && n.dlc10AssignmentStatus !== 'Active'
        ? (n.dlc10AssignmentStatus === 'Pending' ? SMS_REGISTRATION_PENDING_WARNING : SMS_NOT_REGISTERED_WARNING)
        : null;

/** Label for VoIP number selectors: nickname + formatted number, or just the number. */
export function formatVoipNumberLabel(
    e164Number: string,
    displayName?: string | null,
): string {
    const formatted = formatPhoneNumber(e164Number);
    const name = displayName?.trim();
    return name ? `${name} · ${formatted}` : formatted;
}

/**
 * Live-formats a phone input value as the user types.
 * Progressively formats towards (xxx) xxx-xxxx.
 * Accepts raw digits, partial input, or E.164 (+1…) from the API.
 * Returns the original value unchanged for anything outside US patterns.
 */
export function formatPhoneInput(value: string | null | undefined): string {
    if (!value) return '';
    let digits = stripPhoneToDigits(value);

    // US country code — same rule as formatPhoneNumber (handles +1555… from the API)
    if (digits.length === 11 && digits.startsWith('1')) {
        digits = digits.slice(1);
    }
    if (digits.length > 10) return value;
    if (digits.length === 0) return '';

    if (digits.length < 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
